import React, { useState } from 'react';
import { Button, CircularProgress, Icon, useTheme } from '@mui/material';
import { useAppDispatch } from 'src/store';
import { generateActivityNote } from 'src/features/account/contacts.slice';

interface GenerateActivityButtonProps {
  contactId: string;
  onSuccess?: () => void;
  onError?: (error: any) => void;
  variant?: 'contained' | 'outlined' | 'text';
  fullWidth?: boolean;
  className?: string;
  label?: string;
  size?: 'small' | 'medium' | 'large';
  customStyles?: React.CSSProperties;
  sx?: any;
}

/**
 * A reusable button component that generates AI activity summaries for contacts
 */
const GenerateActivityButton: React.FC<GenerateActivityButtonProps> = ({
  contactId,
  onSuccess,
  onError,
  variant = 'outlined',
  fullWidth = false,
  className,
  label = 'AI Summary',
  size = 'small',
  customStyles,
  sx = {}
}) => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(false);

  const handleGenerateNote = async (event: React.MouseEvent<HTMLButtonElement>) => {
    // Prevent default behavior and stop event propagation to parent elements
    event.preventDefault();
    event.stopPropagation();
    
    if (!contactId) {
      console.error('No contact ID provided to GenerateActivityButton');
      return;
    }

    setLoading(true);
    try {
      await dispatch(generateActivityNote({ contactId })).unwrap();
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      console.error('Error generating activity note:', error);
      if (onError) {
        onError(error);
      }
    } finally {
      setLoading(false);
    }
  };

  // Determine height based on size prop
  const getHeight = () => {
    switch (size) {
      case 'small': return '40px';
      case 'large': return '64px';
      default: return '56px';
    }
  };

  return (
    // must pass event to onclick handler
    <Button
      onClick={(event) => handleGenerateNote(event)}
      disabled={loading}
      fullWidth={fullWidth}
      variant={variant}
      className={className}
      startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <Icon sx={{ fontSize: '18px' }}>auto_awesome</Icon>}
      style={customStyles}
      sx={{
        height: getHeight(),
        borderRadius: '8px',
        background: 'transparent',
        color: theme.palette.text.secondary,
        fontWeight: 500,
        fontSize: '13px',
        textTransform: 'none',
        fontFamily: theme.typography.button.fontFamily,
        mb: 1.5,
        px: 2,
        py: 0.75,
        position: 'relative',
        border: '1px solid transparent',
        backgroundImage: `linear-gradient(${theme.palette.background.default}, ${theme.palette.background.default}), ${(theme as any).gradientBorders.subtle(theme.palette.mode)}`,
        backgroundOrigin: 'border-box',
        backgroundClip: 'padding-box, border-box',
        transition: 'all 0.2s ease',
        '&:hover': {
          background: theme.palette.mode === 'dark'
            ? 'rgba(255, 255, 255, 0.05)'
            : 'rgba(0, 0, 0, 0.03)',
          backgroundImage: `linear-gradient(${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)'}, ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)'}), ${(theme as any).gradientBorders.primary(theme.palette.mode)}`,
          backgroundOrigin: 'border-box',
          backgroundClip: 'padding-box, border-box',
          color: theme.palette.text.primary,
        },
        '& .MuiButton-startIcon': {
          marginRight: '6px',
          '& svg': {
            fontSize: '18px'
          }
        },
        '& .MuiCircularProgress-root': {
          color: theme.palette.text.secondary
        },
        ...sx
      }}
    >
      {label}
    </Button>
  );
};

export default GenerateActivityButton; 
